import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DeviceMobile, DownloadSimple, Share, Plus, X, AppleLogo, AndroidLogo } from "@phosphor-icons/react";
import { isStandalone, isIos, isAndroid, canPromptInstall, promptInstall, onInstallStateChange } from "@/lib/pwa";
import { isNative } from "@/lib/native";

const KEY_DISMISSED = "fidelis_pwa_dismissed_at";

const shouldShowAuto = () => {
  if (isNative()) return false;          // never show inside Capacitor APK/IPA
  if (isStandalone()) return false;
  const dismissed = Number(localStorage.getItem(KEY_DISMISSED) || 0);
  if (!dismissed) return true;
  return Date.now() - dismissed > 1000 * 60 * 60 * 24 * 3; // 3 days
};

export const InstallPWAModal = ({ openOnMount = true }) => {
  const [open, setOpen] = useState(false);
  const [hasPrompt, setHasPrompt] = useState(canPromptInstall());

  useEffect(() => {
    const off = onInstallStateChange(({ canInstall }) => setHasPrompt(!!canInstall));
    if (openOnMount && shouldShowAuto()) {
      const t = setTimeout(() => setOpen(true), 1200);
      return () => { clearTimeout(t); off(); };
    }
    return off;
  }, [openOnMount]);

  const dismiss = () => {
    localStorage.setItem(KEY_DISMISSED, String(Date.now()));
    setOpen(false);
  };

  const install = async () => {
    const r = await promptInstall();
    if (r?.outcome === "accepted" || r?.outcome === "unavailable") setOpen(false);
  };

  if (isNative() || isStandalone()) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && dismiss()}>
      <DialogContent className="max-w-md" data-testid="pwa-install-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DeviceMobile size={20} weight="duotone" className="text-blue-600"/>
            Instalar app Zeferius Pay
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-zinc-600">
            Tenha acesso rápido à sua área pelo ícone na tela inicial. Funciona em Android e iOS.
          </p>

          {isIos() ? (
            <IosSteps />
          ) : (
            <div className="rounded-xl border border-zinc-200 p-4 bg-zinc-50/60">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <AndroidLogo size={18} weight="fill" className="text-emerald-600"/> Android / Chrome
              </div>
              {hasPrompt ? (
                <Button onClick={install} data-testid="pwa-install-btn-android" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <DownloadSimple className="mr-2" size={18}/> Adicionar à tela inicial
                </Button>
              ) : (
                <p className="text-sm text-zinc-600">
                  Toque no menu <strong>⋮</strong> do navegador e selecione <strong>"Instalar app"</strong> ou <strong>"Adicionar à tela inicial"</strong>.
                </p>
              )}
            </div>
          )}

          <button
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            className="text-xs text-zinc-500 hover:text-zinc-800 underline w-full text-center"
          >
            Continuar no navegador
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const InstallPWAButton = ({ className = "" }) => {
  const [hasPrompt, setHasPrompt] = useState(canPromptInstall());
  const [open, setOpen] = useState(false);

  useEffect(() => onInstallStateChange(({ canInstall }) => setHasPrompt(!!canInstall)), []);

  if (isNative() || isStandalone()) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="pwa-install-trigger"
        className={`gap-2 ${className}`}
      >
        <DownloadSimple size={16} weight="bold"/> Instalar app
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DeviceMobile size={20} weight="duotone" className="text-blue-600"/>
              Instalar Zeferius Pay
            </DialogTitle>
          </DialogHeader>
          {isIos() ? <IosSteps /> : (
            <div className="space-y-3">
              {hasPrompt ? (
                <Button onClick={async () => { await promptInstall(); setOpen(false); }} data-testid="pwa-install-confirm" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <DownloadSimple className="mr-2" size={18}/> Adicionar à tela inicial
                </Button>
              ) : (
                <div className="text-sm text-zinc-600 rounded-xl border border-zinc-200 p-4 bg-zinc-50/60">
                  No menu <strong>⋮</strong> do navegador, selecione <strong>"Instalar app"</strong>.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const IosSteps = () => (
  <div className="rounded-xl border border-zinc-200 p-4 bg-zinc-50/60 space-y-3">
    <div className="flex items-center gap-2 text-sm font-medium">
      <AppleLogo size={18} weight="fill"/> iOS / Safari
    </div>
    <ol className="space-y-2.5 text-sm text-zinc-700">
      <li className="flex items-start gap-2">
        <span className="w-5 h-5 rounded-full bg-blue-600 text-white grid place-items-center text-[11px] font-semibold flex-shrink-0">1</span>
        Toque no botão <Share weight="bold" className="inline mx-1" size={14}/> <strong>Compartilhar</strong> na barra do Safari.
      </li>
      <li className="flex items-start gap-2">
        <span className="w-5 h-5 rounded-full bg-blue-600 text-white grid place-items-center text-[11px] font-semibold flex-shrink-0">2</span>
        Role a lista e selecione <Plus weight="bold" className="inline mx-1" size={14}/> <strong>Adicionar à Tela de Início</strong>.
      </li>
      <li className="flex items-start gap-2">
        <span className="w-5 h-5 rounded-full bg-blue-600 text-white grid place-items-center text-[11px] font-semibold flex-shrink-0">3</span>
        Confirme em <strong>Adicionar</strong> e abra o app pelo ícone Zeferius Pay.
      </li>
    </ol>
  </div>
);

export default InstallPWAModal;
